import React from 'react';
import { logoutCleanup } from '../../auth';
import type { LaunchDarklyFlags } from '../../common/launchDarkly';
import type { ISigner } from '../../common/recipients/types';
import { checkIfRecipientIsSender, useIsSkySlopeMobileApp } from '../../common/utils';
import { signerUiUrl } from '../../lib/environment-config';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import * as actions from '../../store/senderBuild/actions';
import { isSmartListingUrl } from '../envelope-template-builder/navigation-utils';

export function useSignNow({
  flags,
  userInfo,
  shouldShowSellersShieldBanner,
}: {
  flags?: LaunchDarklyFlags;
  userInfo: any;
  shouldShowSellersShieldBanner?: boolean;
}) {
  const { envelopeId, signers, signerToken, accountId, config } = useShallowSelector((state) => ({
    envelopeId: state.senderBuild.id,
    signers: state.senderBuild.signers,
    signerToken: state.senderBuild.signerToken,
    accountId: state.senderBuild.accountId,
    config: state.senderBuild.config,
  }));

  const dispatch = useDispatch();
  const isMobileApp = useIsSkySlopeMobileApp();
  const signNowFlag = flags && flags['sign-now'];
  const hasAutoSignedRef = React.useRef(false);

  // 1. Determine if the user is eligible to use the "Sign Now" feature
  const signNowSigner = React.useMemo(() => {
    // Impersonated callers are not eligible, and the check belongs here, not on
    // the button: senderEmail is the owner's under impersonation so the match
    // below succeeds for them,
    // and the smart-listing effect and native bridge both read
    // isAllowedToSignNow without ever looking at showButton.
    if (!userInfo || !signNowFlag || userInfo.isDelegate) return;

    let signer: undefined | ISigner;
    for (const s of signers) {
      if (checkIfRecipientIsSender(s as ISigner) && userInfo.senderEmail === s.email && s.signingGroup === 'default') {
        signer = s as ISigner;
      }
    }

    return signer;
  }, [signNowFlag, signers, userInfo]);
  const isAllowedToSignNow = Boolean(signNowSigner);

  // 2. Kick off the sign now process by fetching the signer token (e.g. when the user clicks "sign now" button)
  const signNow = React.useCallback(() => {
    if (!envelopeId || !signNowSigner || !userInfo) return;

    const fetchTokenReq = {
      envelopeId,
      signerId: signNowSigner.signerId,
      signerEmail: userInfo.senderEmail,
      ...(accountId && accountId.trim() !== '' && { accountId }),
    };

    dispatch(actions.fetchSignerToken(fetchTokenReq));
  }, [accountId, dispatch, envelopeId, signNowSigner, userInfo]);

  // 3. Once the signer token has been fetched, redirect to the signer UI
  React.useEffect(() => {
    if (!signerToken.length || !envelopeId) return;

    const hasSmartListing = isSmartListingUrl(config.senderUi?.sendRedirectUrl);

    if (config.senderUi?.sendRedirectUrl && hasSmartListing) {
      // For smart-listing, append signerToken and sentEnvelopeGuid to the redirect URL
      const redirectUrl = new URL(config.senderUi.sendRedirectUrl);
      redirectUrl.searchParams.set('signerToken', signerToken);
      redirectUrl.searchParams.set('sentEnvelopeGuid', envelopeId);

      if (shouldShowSellersShieldBanner) {
        redirectUrl.searchParams.set('sellersShieldBannerShown', 'true');
      }

      logoutCleanup();
      window.location.href = redirectUrl.toString();
    } else {
      const signNowUrl = new URL(signerUiUrl);
      signNowUrl.searchParams.set('signNow', 'true');

      if (shouldShowSellersShieldBanner) {
        signNowUrl.searchParams.set('sellersShieldBannerShown', 'true');
      }

      signNowUrl.hash = `t=${signerToken}`;
      window.location.href = signNowUrl.toString();
    }
  }, [config?.senderUi?.sendRedirectUrl, signerToken, envelopeId, shouldShowSellersShieldBanner]);

  // 4. Auto-trigger Sign Now ONLY for smart-listings redirects
  // This skips the Sign Now button/page and goes straight to signing
  React.useEffect(() => {
    const redirectUrl = config.senderUi?.sendRedirectUrl;
    const hasSmartListing = isSmartListingUrl(redirectUrl);
    const shouldAutoSign = !signerToken.length && isAllowedToSignNow && !hasAutoSignedRef.current && hasSmartListing;

    if (shouldAutoSign) {
      // Fetch the signer token, which will fire the useEffect in #3 👆
      hasAutoSignedRef.current = true;
      signNow();
    }
  }, [signerToken, isAllowedToSignNow, signNow, config.senderUi?.sendRedirectUrl]);

  // 5. Assign signNow to mobile app interface for native bridge communication
  React.useEffect(() => {
    if (!isAllowedToSignNow) return undefined;
    window.mobileApp.signNow = signNow;
    // The shell calls whatever was last assigned, so a closure left behind
    // after eligibility drops would still sign as the owner.
    return () => {
      if (window.mobileApp.signNow === signNow) {
        window.mobileApp.signNow = () => {};
      }
    };
  }, [isAllowedToSignNow, signNow]);

  return {
    showButton: Boolean(isAllowedToSignNow && !isMobileApp),
    mutate: signNow,
    isAllowedToSignNow,
  };
}
